import { formatDate, formatDateDisplay, toDateString } from '@/utils/date';

export type DateRangePresetId =
    | '24h'
    | 'last3days'
    | 'last7days'
    | 'last14days'
    | 'last4weeks'
    | 'last3months'
    | 'all'
    | 'none';

export interface DateRangePreset {
    id: DateRangePresetId;
    labelKey: string;
    calculateDates: () => { dateFrom: string | undefined; dateTo: string | undefined };
    getDateRangeDisplay: () => string;
}

/**
 * The `from` the picker sends for the "show all" preset.
 *
 * The preset itself calculates no dates, but the picker substitutes this sentinel so
 * endpoints that require a bounded range keep working (see the TODO on
 * `presetRange`). Exported so a consumer whose endpoint DOES support an open range
 * can recognise it and omit `from` instead of asking for a 56-year period.
 */
export const ALL_TIME_SENTINEL_FROM = '1970-01-01';

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
    {
        id: '24h',
        labelKey: 'molecules.dateRangePicker.24h',
        calculateDates: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 1);
            return {
                dateFrom: toDateString(from),
                dateTo: toDateString(to),
            };
        },
        getDateRangeDisplay: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 1);
            return `${formatDateDisplay(from)} - ${formatDateDisplay(to)}`;
        },
    },
    {
        id: 'last3days',
        labelKey: 'molecules.dateRangePicker.last3days',
        calculateDates: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 2);
            return {
                dateFrom: toDateString(from),
                dateTo: toDateString(to),
            };
        },
        getDateRangeDisplay: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 2);
            return `${formatDateDisplay(from)} - ${formatDateDisplay(to)}`;
        },
    },
    {
        id: 'last7days',
        labelKey: 'molecules.dateRangePicker.last7days',
        calculateDates: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 6);
            return {
                dateFrom: toDateString(from),
                dateTo: toDateString(to),
            };
        },
        getDateRangeDisplay: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 6);
            return `${formatDateDisplay(from)} - ${formatDateDisplay(to)}`;
        },
    },
    {
        id: 'last14days',
        labelKey: 'molecules.dateRangePicker.last14days',
        calculateDates: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 13);
            return {
                dateFrom: toDateString(from),
                dateTo: toDateString(to),
            };
        },
        getDateRangeDisplay: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 13);
            return `${formatDateDisplay(from)} - ${formatDateDisplay(to)}`;
        },
    },
    {
        id: 'last4weeks',
        labelKey: 'molecules.dateRangePicker.last4weeks',
        calculateDates: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 27);
            return {
                dateFrom: toDateString(from),
                dateTo: toDateString(to),
            };
        },
        getDateRangeDisplay: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 27);
            return `${formatDateDisplay(from)} - ${formatDateDisplay(to)}`;
        },
    },
    {
        id: 'last3months',
        labelKey: 'molecules.dateRangePicker.last3months',
        calculateDates: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 90);
            return {
                dateFrom: toDateString(from),
                dateTo: toDateString(to),
            };
        },
        getDateRangeDisplay: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - 90);
            return `${formatDateDisplay(from)} - ${formatDateDisplay(to)}`;
        },
    },
    {
        id: 'all',
        labelKey: 'molecules.dateRangePicker.all',
        calculateDates: () => ({
            dateFrom: undefined,
            dateTo: undefined,
        }),
        getDateRangeDisplay: () => {
            return '';
        },
    },
    {
        id: 'none',
        labelKey: 'molecules.dateRangePicker.loading',
        calculateDates: () => ({
            dateFrom: undefined,
            dateTo: undefined,
        }),
        getDateRangeDisplay: () => {
            return '';
        },
    },
];

export interface FindMatchingPresetOptions {
    /**
     * Whether `24h` may match. A picker that does not offer it passes false, so a
     * yesterday..today range reads as its days rather than as a preset the page lacks.
     */
    include24h?: boolean;
    /** Whether `last3days` may match. Hidden presets must not claim a range. */
    includeLast3Days?: boolean;
    /** Presets the menu omits. A matching range is shown as its days instead. */
    hiddenPresetIds?: readonly DateRangePresetId[];
}

/**
 * Check if the provided dates match any of the available presets
 * @param from - The start date in string format
 * @param to - The end date in string format
 * @param options - Which presets may match
 * @returns The matching preset ID or null if no match is found
 */
export function findMatchingPreset(
    from: string | undefined,
    to: string | undefined,
    options: FindMatchingPresetOptions = {}
): DateRangePresetId | null {
    const { include24h = true, includeLast3Days = false, hiddenPresetIds = [] } = options;
    if (!from || !to) {
        return null;
    }
    const hidden = new Set(hiddenPresetIds);

    // Special case for 'all' preset (uses 1970-01-01 as from date)
    if (from === '1970-01-01' && !hidden.has('all')) {
        const todayStr = formatDate(new Date(), 'internal');
        if (to === todayStr) {
            return 'all';
        }
    }

    // Check other presets
    for (const preset of DATE_RANGE_PRESETS) {
        if (preset.id === 'all') {
            continue; // Skip 'all' preset as it's handled above
        }
        if (hidden.has(preset.id)) {
            continue;
        }
        if (preset.id === '24h' && !include24h) {
            continue;
        }
        if (preset.id === 'last3days' && !includeLast3Days) {
            continue;
        }
        const dates = preset.calculateDates();
        if (dates.dateFrom === from && dates.dateTo === to) {
            return preset.id;
        }
    }

    return null;
}
